import { useMemo } from 'react';
import './AmbientParticles.css';

/**
 * Casa de Candy — softly drifting ambient particles with depth variance.
 *
 * - Three depth layers: distant (smallest, slowest, dimmest),
 *   mid (medium speed/size), and near (rare, slightly brighter).
 * - Movement is non-linear and irregular (each dot has its own
 *   delay, duration, drift path) so it never reads as snowfall.
 * - Opacity is intentionally restrained (max 0.18) so readability
 *   never suffers and content stays the hero.
 *
 * Pure CSS animation — no JS rAF loop, no layout thrash. The
 * component sits behind content via `position: absolute; inset: 0;
 * pointer-events: none;` and respects `prefers-reduced-motion`.
 */
export function AmbientParticles({
  count = 28,
  className = '',
  density = 'standard',
  testid = 'ambient-particles',
}) {
  // Pre-compute particle config once per mount so the layout is stable.
  const particles = useMemo(() => {
    const total = density === 'dense' ? Math.round(count * 1.6) : count;
    const items = [];
    for (let i = 0; i < total; i += 1) {
      // Layer split: 55% distant, 30% mid, 15% near
      const r = Math.random();
      const layer = r < 0.55 ? 'far' : r < 0.85 ? 'mid' : 'near';
      const size =
        layer === 'far'
          ? 1 + Math.random() * 1.2
          : layer === 'mid'
          ? 1.6 + Math.random() * 1.4
          : 2.2 + Math.random() * 1.6;
      const baseOpacity =
        layer === 'far' ? 0.06 : layer === 'mid' ? 0.1 : 0.16;
      const duration =
        layer === 'far'
          ? 28 + Math.random() * 18
          : layer === 'mid'
          ? 22 + Math.random() * 14
          : 16 + Math.random() * 12;
      // Subtle horizontal drift in a cross-axis sin pattern.
      const driftX = (Math.random() * 60 - 30).toFixed(1) + 'px';
      const startTop = (Math.random() * 110 - 5).toFixed(1) + '%';
      const left = (Math.random() * 100).toFixed(1) + '%';
      const delay = (-Math.random() * duration).toFixed(2) + 's';
      // Tint: most are warm pink/violet, a few cool cyan to add depth.
      const tint =
        Math.random() < 0.18
          ? 'rgba(180, 220, 255, 1)'
          : Math.random() < 0.55
          ? 'rgba(255, 180, 216, 1)'
          : 'rgba(208, 184, 255, 1)';
      items.push({
        id: i,
        layer,
        size: size.toFixed(2),
        opacity: baseOpacity,
        duration: duration.toFixed(2),
        driftX,
        startTop,
        left,
        delay,
        tint,
      });
    }
    return items;
  }, [count, density]);

  return (
    <div
      className={`ambient-particles ${className}`}
      aria-hidden="true"
      data-testid={testid}
    >
      {particles.map((p) => (
        <span
          key={p.id}
          className={`ambient-particles__dot ambient-particles__dot--${p.layer}`}
          style={{
            '--size': `${p.size}px`,
            '--start-top': p.startTop,
            '--drift-x': p.driftX,
            '--duration': `${p.duration}s`,
            '--delay': p.delay,
            '--peak-opacity': p.opacity,
            '--tint': p.tint,
            left: p.left,
          }}
        />
      ))}
    </div>
  );
}

export default AmbientParticles;
